import Component from '@glimmer/component';
import { t } from 'ember-intl';
import can from 'ember-can/helpers/can';
import {
  HdsPageHeader,
  HdsTable,
  HdsIcon,
  HdsDropdown,
} from '@hashicorp/design-system-components/components';
import {
  ZeroState,
  CoreLayoutMain,
  BreadcrumbsContainer,
  Flex,
} from 'core/components';
import {
  optionForSourceState,
  labelForRegion,
  resourceIsBusy,
  resourceTypeFor,
  optionForResourceService,
} from 'core/helpers';
import CreateClusterButton from 'hcp/components/create-cluster-button';
import LocationLink from 'hcp/components/location-link';

/**
 *
 * `PageHvnConnectedClusters` description here.
 *
 *
 * ```
 * <Page::HvnConnectedClusters
 *   @model={{@model}}
 * />
 * ```
 *
 * @class PageHvnConnectedClusters
 *
 */
export default class PageHvnConnectedClusters extends Component {
  /**
   *
   * `model` will be a model containing the network dependencies (clusters);
   * @argument model;
   * @type {Object}
   *
   */
  <template>
    <CoreLayoutMain>
      <:header>
        <HdsPageHeader as |PH|>
          <PH.Title data-test-hvn-connected-cluster-title>{{t
              "components.page.hvn-connections.list.connected-clusters.title"
            }}</PH.Title>
          <PH.Breadcrumb>
            <BreadcrumbsContainer />
          </PH.Breadcrumb>
          {{#unless
            (resourceIsBusy @model.network type=(resourceTypeFor "network"))
          }}
            <PH.Actions>
              <CreateClusterButton @model={{@model}} />
            </PH.Actions>
          {{/unless}}
        </HdsPageHeader>
      </:header>
      <:body>
        <div class="page-hvn-connected-clusters">
          {{#if @model.dependencies}}
            <HdsTable data-test-connected-clusters-table>
              <:head as |H|>
                <H.Tr>
                  <H.Th data-test-connected-clusters-table-column-resource>{{t
                      "components.page.hvn-connections.list.connected-clusters.resource"
                    }}</H.Th>
                  <H.Th data-test-connected-clusters-table-column-service>{{t
                      "components.page.hvn-connections.list.connected-clusters.service"
                    }}</H.Th>
                  <H.Th data-test-connected-clusters-table-column-provider>{{t
                      "components.page.hvn-connections.list.connected-clusters.provider"
                    }}</H.Th>
                  <H.Th data-test-connected-clusters-table-column-region>{{t
                      "components.page.hvn-connections.list.connected-clusters.region"
                    }}</H.Th>
                </H.Tr>
              </:head>
              <:body as |B|>
                {{#each @model.dependencies as |link|}}
                  <B.Tr data-test-connected-clusters-table-row>
                    <B.Td data-test-connected-clusters-table-cell>
                      <LocationLink
                        @link={{link}}
                        @hideIcon={{true}}
                        @color="secondary"
                        class="hds-font-weight-medium"
                      /></B.Td>
                    <B.Td data-test-connected-clusters-table-cell>
                      {{#let (optionForResourceService link) as |option|}}
                        <Flex @alignItems="center" @gap="xs">
                          <HdsIcon @name={{option.icon}} @isInline={{true}} />
                          <span>{{option.name}}</span>
                        </Flex>
                      {{/let}}
                    </B.Td>
                    <B.Td data-test-connected-clusters-table-cell>{{#let
                        (optionForSourceState
                          "region" link.location.region.provider
                        )
                        as |option|
                      }}
                        <Flex @alignItems="center" @direction="row" @gap="2xs">
                          <HdsIcon @name={{option.icon}} @isInline={{true}} />
                          <span class="hds-typography-body-200">
                            {{t option.text}}
                          </span>
                        </Flex>
                      {{/let}}
                    </B.Td>
                    <B.Td data-test-connected-clusters-table-cell>
                      <span
                        data-test-connected-clusters-table-cell-region-display
                      >
                        {{t
                          (labelForRegion
                            link.location.region.region
                            provider=link.location.region.provider
                          )
                        }}
                      </span>
                      <span data-test-connected-clusters-table-cell-region-name>
                        ({{link.location.region.region}})
                      </span>
                    </B.Td>
                  </B.Tr>
                {{/each}}
              </:body>
            </HdsTable>
          {{else}}
            <ZeroState data-test-connected-clusters-zero-state as |ZS|>
              <ZS.Header>
                {{t
                  "components.page.hvn-connections.list.connected-clusters.empty.header"
                }}
              </ZS.Header>
              <ZS.Message>
                {{t
                  "components.page.hvn-connections.list.connected-clusters.empty.message"
                }}
              </ZS.Message>
              {{#if (can "create network-peering" @model.peerings)}}
                <ZS.Action>
                  <HdsDropdown @listPosition="bottom-right" as |dd|>
                    <dd.ToggleButton
                      @color="primary"
                      @text={{t
                        "components.page.hvn-connections.list.connected-clusters.empty.action"
                      }}
                      data-test-create-connection-button
                    />
                    <dd.Interactive
                      @text={{t
                        "components.page.hvns.detail.create-dropdown.guided-flow.peering.title"
                      }}
                      @route="cloud.orgs.detail.projects.detail.hvns.detail.peerings.create"
                      @model={{@model.network.id}}
                      @icon="connection"
                      data-test-create-peering
                    >
                      {{t
                        "components.page.hvns.detail.create-dropdown.guided-flow.peering.title"
                      }}
                    </dd.Interactive>
                    <dd.Description
                      @text={{t
                        "components.page.hvns.detail.create-dropdown.guided-flow.peering.description"
                        provider=@model.network.location.region.provider
                      }}
                    />
                    <dd.Separator />
                    <dd.Interactive
                      @text={{t
                        "components.page.hvns.detail.create-dropdown.guided-flow.tgw.title"
                      }}
                      @route="cloud.orgs.detail.projects.detail.hvns.detail.transit-gateway-attachments.create"
                      @model={{@model.network.id}}
                      @icon="connection-gateway"
                      data-test-create-tgw
                    />
                    <dd.Description
                      @text={{t
                        "components.page.hvns.detail.create-dropdown.guided-flow.tgw.description"
                      }}
                    />
                  </HdsDropdown>
                </ZS.Action>
              {{/if}}
            </ZeroState>
          {{/if}}
        </div>
      </:body>
    </CoreLayoutMain>
  </template>
}
